import React, { useState, useEffect, useRef } from 'react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import ConfettiExplosion from 'react-confetti-explosion';
import { CheckCircleIcon, ClockIcon, DocumentTextIcon } from '@heroicons/react/solid';
import { XIcon } from '@heroicons/react/outline';

/**
 * RmsAllFormsCompletedModal
 * -------------------------
 * Purpose:
 *   Info-only modal displayed when a service user (school or guardian) completes all their
 *   referral + HFP forms. Shows celebration confetti and provides dynamic "next steps"
 *   guidance based on the other party's completion status.
 * 
 * Business Rules:
 *   - School user completes: 4 referral forms + 8 HFP forms = 12 total
 *   - Guardian completes: 3 referral forms + 8 HFP forms = 11 total
 *   - HFP report requires BOTH parties to complete all HFP forms
 *   - When one party finishes first, they see "waiting for other party" message
 *   - When both complete, show CTA to view the HFP report
 * 
 * Design:
 *   - Uses RMS brand colours (primary teal) instead of violet/purple
 *   - Confetti celebration on modal open
 *   - Simple info-only display with appropriate CTA based on HFP readiness
 * 
 * Props:
 *   - isOpen: Whether the modal is visible
 *   - onClose: Callback when modal is closed
 *   - onViewHfp: Callback to open the HFP report dialog (optional)
 *   - otherPartyComplete: Boolean - has the other party completed their forms?
 *   - otherPartyRole: 'school_user' | 'guardian' - who is the other party
 *   - isSecondaryReferral: Boolean - secondary-school referrals skip the HFP
 *     entirely, so the copy must not mention it; once both parties finish their
 *     referral forms the CYP is simply ready for triage.
 *   - viewerRole: 'school_user' | 'guardian' - current user's role (kept for future use)
 *   - patientRecordId: Patient record ID (kept for future use)
 *   - orgId: Organisation ID (kept for future use)
 *   - patientName: CYP's name for personalization (optional)
 *   - onFeedbackSubmitted: Callback after feedback is saved (kept for future use)
 */

// Custom animations as a style tag
const customStyles = `
  @keyframes rms-fadeIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  
  @keyframes rms-scaleIn {
    from { opacity: 0; transform: translate(-50%, -50%) scale(0.95); }
    to { opacity: 1; transform: translate(-50%, -50%) scale(1); }
  }
  
  .rms-animate-fadeIn {
    animation: rms-fadeIn 0.3s ease-out;
  }
  
  .rms-animate-scaleIn {
    animation: rms-scaleIn 0.3s ease-out;
  }
`;

export default function RmsAllFormsCompletedModal({
  isOpen,
  onClose,
  onViewHfp,
  otherPartyComplete = false,
  otherPartyRole = 'guardian',
  isSecondaryReferral = false,
  // Props kept for future feedback feature re-enablement
  viewerRole = 'school_user',
  patientRecordId,
  orgId,
  patientName,
  onFeedbackSubmitted
}) {
  const [showConfetti, setShowConfetti] = useState(false);
  const modalContentRef = useRef(null);

  // Trigger confetti when modal opens
  useEffect(() => {
    if (isOpen) {
      setShowConfetti(true);
      
      // Stop confetti after a delay
      const timer = setTimeout(() => setShowConfetti(false), 3500);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Get human-readable label for the other party
  const getOtherPartyLabel = () => {
    if (otherPartyRole === 'guardian') {
      return 'parent / carer';
    }
    return 'school representative';
  };

  // Get the display name for the CYP
  const getCypName = () => {
    return patientName || 'the child or young person';
  };

  /**
   * Render the "Next Steps" section based on completion status
   * - If other party is complete: show HFP ready message with View HFP CTA
   * - If other party is not complete: show waiting message
   */
  const renderNextSteps = () => {
    // Secondary-school GUARDIAN: completing the referral forms is only part 1 —
    // the triage (additional information) forms come next, auto-assigned and
    // independent of the school rep's progress. Keep the message simple and
    // never claim everything is complete (TD: the old copy celebrated "all
    // questionnaires completed" while the triage forms were still to do).
    if (isSecondaryReferral && viewerRole === 'guardian') {
      return (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6 rms-animate-fadeIn">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <DocumentTextIcon className="w-6 h-6 text-green-600" />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-green-900 mb-1">
                Part 1 is complete
              </h4>
              <p className="text-sm text-green-700">
                The next stage is the triage forms, which you can start from this page now.
              </p>
            </div>
          </div>
        </div>
      );
    }
    if (otherPartyComplete) {
      // Both parties have completed - show CTA to view HFP
      return (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6 rms-animate-fadeIn">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <DocumentTextIcon className="w-6 h-6 text-green-600" />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-green-900 mb-1">
                {isSecondaryReferral
                  ? <>Great news! {getCypName()} is ready for triage</>
                  : 'Great news! The Holistic Functioning Profile is ready'}
              </h4>
              <p className="text-sm text-green-700">
                {isSecondaryReferral
                  ? <>All respondents have completed their referral forms, so {getCypName()} is now ready for triage.</>
                  : <>All respondents have completed their referral forms. You can now view {getCypName()}'s comprehensive profile.</>}
              </p>
            </div>
          </div>
        </div>
      );
    } else {
      // Other party hasn't completed yet - show waiting message
      return (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 rms-animate-fadeIn">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <ClockIcon className="w-6 h-6 text-amber-600" />
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-amber-900 mb-1">
                Waiting for the {getOtherPartyLabel()}
              </h4>
              <p className="text-sm text-amber-700">
                {isSecondaryReferral
                  ? <>The {getOtherPartyLabel()} still needs to complete their forms. Once they have, {getCypName()} will be ready for triage.</>
                  : <>The {getOtherPartyLabel()} still needs to complete their forms. Once they have completed theirs, the Holistic Functioning Profile for {getCypName()} will be ready.</>}
              </p>
              <p className="text-sm text-amber-600 mt-2 italic">
                {isSecondaryReferral
                  ? "We'll let you know once everything's complete."
                  : "We'll email you as soon as it's ready to view."}
              </p>
            </div>
          </div>
        </div>
      );
    }
  };

  /**
   * Render the primary CTA button based on HFP readiness
   * - If HFP ready: "View Holistic Functioning Profile" button
   * - If HFP not ready: "Close" button
   */
  const renderPrimaryCta = () => {
    // Secondary referrals have no HFP to view — always show the plain Close CTA.
    if (!isSecondaryReferral && otherPartyComplete && onViewHfp) {
      return (
        <button
          type="button"
          onClick={() => {
            onViewHfp();
            onClose();
          }}
          className="w-full py-3 px-4 bg-gradient-to-r from-primary to-teal-600 text-white rounded-lg font-medium hover:from-teal-700 hover:to-teal-800 transition-all duration-200 ease-out transform hover:scale-[1.02] active:scale-[0.98] shadow-md hover:shadow-lg flex items-center justify-center gap-2"
        >
          <DocumentTextIcon className="w-5 h-5" />
          View Holistic Functioning Profile
        </button>
      );
    } else {
      return (
        <button
          type="button"
          onClick={onClose}
          className="w-full py-3 px-4 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-colors"
        >
          Close
        </button>
      );
    }
  };

  return (
    <>
      {/* Inject custom styles */}
      <style>{customStyles}</style>
      
      <AlertDialog.Root open={isOpen}>
        <AlertDialog.Portal>
          <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/60 z-50 backdrop-blur-sm" />
          <AlertDialog.Content 
            ref={modalContentRef}
            className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-2xl shadow-2xl p-6 sm:p-8 w-[95vw] sm:w-[90vw] max-w-lg max-h-[90vh] overflow-y-auto z-50"
          >
            <div className="relative">
              {/* Confetti explosion */}
              {showConfetti && (
                <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50">
                  <ConfettiExplosion 
                    particleCount={200}
                    width={1600}
                    colors={['#0D9488', '#14B8A6', '#2DD4BF', '#5EEAD4', '#99F6E4', '#F59E0B', '#10B981']}
                    duration={3000}
                  />
                </div>
              )}
              
              {/* Header Section */}
              <div className="text-center mb-6">
                <div className="flex justify-center mb-3">
                  <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center">
                    <CheckCircleIcon className="w-9 h-9 text-primary" />
                  </div>
                </div>
                
                <AlertDialog.Title className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 leading-tight">
                  Amazing work! 🎉
                </AlertDialog.Title>
                
                <AlertDialog.Description className="text-gray-600 text-sm sm:text-base mb-2 leading-relaxed">
                  {isSecondaryReferral && viewerRole === 'guardian'
                    // The guardian's referral forms are only part 1 - the triage
                    // (additional information) forms come next, so the copy must
                    // not read as if everything is finished.
                    ? "You've completed the referral forms - now we just need some more information from you."
                    : "You've completed all your questionnaires - that's a big achievement!"}
                </AlertDialog.Description>
                
                <AlertDialog.Description className="text-gray-600 text-sm sm:text-base leading-relaxed">
                  Thank you for taking the time to share your perspective on {getCypName()}.
                </AlertDialog.Description>
              </div>

              {/* Next Steps Section */}
              {renderNextSteps()}

              {/* Primary CTA Button */}
              {renderPrimaryCta()}
            </div>
            
            {/* Close X button in top right */}
            <AlertDialog.Cancel asChild>
              <button
                onClick={onClose}
                className="absolute top-4 right-4 sm:top-6 sm:right-6 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all"
                aria-label="Close"
              >
                <XIcon className="w-5 h-5" />
              </button>
            </AlertDialog.Cancel>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>
    </>
  );
}
